import React, { useState, useEffect } from 'react';

const C = {
  bg:'#1a1c28', card:'#252838', card2:'#2a2d40', border:'#2e3150',
  text:'#e2e8f0', muted:'#64748b', accent:'#f97316', green:'#4ade80',
  red:'#f87171', blue:'#60a5fa', amber:'#fbbf24', purple:'#a78bfa',
};

const CATEGORIES = ['Supplier','Manufacturer','Distributor','Service Provider','Rental','Parts','Other'];
const PAYMENT_TERMS = ['Net 30','Net 60','Net 90','Due on Receipt','COD','Prepaid'];
const DOC_TYPES = ['Warranty','Invoice','Contract','Insurance','COI','Manual','Other'];

function StarRating({ value, onChange }) {
  return (
    <div style={{ display:'flex', gap:3 }}>
      {[1,2,3,4,5].map(n => (
        <span key={n} onClick={() => onChange && onChange(n)} style={{ fontSize:16, cursor:onChange?'pointer':'default', color: n<=value ? C.amber : C.border }}>★</span>
      ))}
    </div>
  );
}

export default function VendorsTab({ axios }) {
  const [vendors,    setVendors]    = useState([]);
  const [selected,   setSelected]   = useState(null);
  const [detail,     setDetail]     = useState(null);
  const [detailTab,  setDetailTab]  = useState('overview');
  const [loading,    setLoading]    = useState(true);
  const [detLoading, setDetLoading] = useState(false);
  const [showForm,   setShowForm]   = useState(false);
  const [editing,    setEditing]    = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [search,     setSearch]     = useState('');
  const [filterCat,  setFilterCat]  = useState('');
  const [newNote,    setNewNote]     = useState('');
  const [newDoc,     setNewDoc]      = useState({ name:'', type:'Other', file_url:'', expiry_date:'', notes:'' });
  const [addingDoc,  setAddingDoc]   = useState(false);
  const [linkData,   setLinkData]    = useState({ clients:[], equipment:[] });
  const [selEquip,   setSelEquip]    = useState([]);
  const [selClients, setSelClients]  = useState([]);
  const [equipSearch,setEquipSearch] = useState('');
  const [clientSearch,setClientSearch] = useState('');
  const [linkSaving, setLinkSaving]  = useState(false);
  const [nameSuggestions, setNameSuggestions] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [showClientPicker, setShowClientPicker] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [flash, setFlash] = useState('');
  const showMsg = (m) => { setFlash(m); setTimeout(() => setFlash(''), 6000); };

  const blank = { name:'', contact:'', phone:'', email:'', website:'', notes:'', category:'Supplier', account_number:'', payment_terms:'Net 30', rating:0, address:'', city:'', state:'', zip:'' };
  const [form, setForm] = useState(blank);

  const inp = { padding:'8px 10px', borderRadius:6, border:`1px solid ${C.border}`, background:'#181928', color:C.text, fontSize:13, width:'100%', boxSizing:'border-box' };

  const load = async (includeArchived = showArchived) => {
    setLoading(true);
    try {
      const r = await axios.get('/api/rental-assets/vendors' + (includeArchived ? '?include_archived=1' : ''));
      setVendors(r.data.vendors || []);
    } catch (e) { showMsg(e.response?.data?.error || 'Could not load vendors'); }
    setLoading(false);
  };

  const loadDetail = async (id) => {
    setDetLoading(true);
    try {
      const r = await axios.get('/api/rental-assets/vendors/' + id);
      setDetail(r.data);
    } catch(e) {}
    setDetLoading(false);
  };

  useEffect(() => {
    load(showArchived);
    axios.get('/api/rentals/clients').then(r => setAllClients(r.data.clients || [])).catch(()=>{});
  }, [showArchived]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (selected) {
      loadDetail(selected);
      axios.get('/api/rental-assets/vendors/' + selected + '/link-data').then(r => {
        setLinkData(r.data);
      }).catch(()=>{});
    }
  }, [selected]);

  const selectVendor = (id) => { setSelected(id); setDetailTab('overview'); setDetail(null); setSelEquip([]); setSelClients([]); };

  useEffect(() => {
    if (detail) {
      setSelEquip((detail.equipment||[]).map(e => e.id));
      setSelClients((detail.clients||[]).map(c => c.id));
    }
  }, [detail]);

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editing) {
        const r = await axios.patch('/api/rental-assets/vendors/' + editing, form);
        setVendors(p => p.map(v => v.id === editing ? r.data.vendor : v));
        if (selected === editing) loadDetail(editing);
      } else {
        const r = await axios.post('/api/rental-assets/vendors', form);
        setVendors(p => [...p, r.data.vendor]);
        setSelected(r.data.vendor.id);
      }
      setShowForm(false); setEditing(null); setForm(blank);
    } catch(e) { alert(e.response?.data?.error || 'Failed to save'); }
    setSaving(false);
  };

  // Delete: the server deletes a vendor with no history and ARCHIVES one
  // that has purchase orders, links, documents or notes — and says which.
  const del = async (id) => {
    const v = vendors.find(x => x.id === id);
    if (!window.confirm(`Delete ${v?.name || 'this vendor'}? If it has purchase orders or history it will be archived instead (hidden, history kept).`)) return;
    try {
      const r = await axios.delete('/api/rental-assets/vendors/' + id);
      showMsg(r.data.message || (r.data.action === 'archived' ? 'Vendor archived' : 'Vendor deleted'));
      if (r.data.action === 'archived' && showArchived) {
        setVendors(p => p.map(x => x.id === id ? { ...x, is_active: false, archived_at: new Date().toISOString() } : x));
      } else {
        setVendors(p => p.filter(x => x.id !== id));
      }
      if (selected === id) { setSelected(null); setDetail(null); }
      setShowForm(false); setEditing(null);
    } catch (e) { showMsg(e.response?.data?.error || 'Could not delete vendor'); }
  };

  const restore = async (id) => {
    try {
      const r = await axios.post('/api/rental-assets/vendors/' + id + '/restore');
      setVendors(p => p.map(x => x.id === id ? r.data.vendor : x));
      showMsg(`${r.data.vendor.name} restored.`);
    } catch (e) { showMsg(e.response?.data?.error || 'Could not restore vendor'); }
  };

  const addLog = async () => {
    if (!newNote.trim() || !selected) return;
    const r = await axios.post('/api/rental-assets/vendors/' + selected + '/logs', { note: newNote.trim() });
    setDetail(p => ({ ...p, logs: [r.data.log, ...(p.logs||[])] }));
    setNewNote('');
  };

  const deleteLog = async (logId) => {
    await axios.delete('/api/rental-assets/vendors/' + selected + '/logs/' + logId);
    setDetail(p => ({ ...p, logs: p.logs.filter(l => l.id !== logId) }));
  };

  const addDoc = async () => {
    if (!newDoc.name.trim() || !selected) return;
    const r = await axios.post('/api/rental-assets/vendors/' + selected + '/docs', newDoc);
    setDetail(p => ({ ...p, docs: [r.data.doc, ...(p.docs||[])] }));
    setNewDoc({ name:'', type:'Other', file_url:'', expiry_date:'', notes:'' });
    setAddingDoc(false);
  };

  const deleteDoc = async (docId) => {
    await axios.delete('/api/rental-assets/vendors/' + selected + '/docs/' + docId);
    setDetail(p => ({ ...p, docs: p.docs.filter(d => d.id !== docId) }));
  };

  const saveEquipmentLinks = async () => {
    setLinkSaving(true);
    try {
      const r = await axios.patch('/api/rental-assets/vendors/' + selected + '/link-equipment', { equipment_ids: selEquip });
      await loadDetail(selected);
      showMsg(`Equipment links saved: ${r.data.linked?.length || 0} linked.`);
    } catch(e) { showMsg(e.response?.data?.error || 'Failed to save equipment links'); }
    setLinkSaving(false);
  };

  const saveClientLinks = async () => {
    setLinkSaving(true);
    try {
      const r = await axios.patch('/api/rental-assets/vendors/' + selected + '/link-clients', { client_ids: selClients });
      await loadDetail(selected);
      showMsg(`Client links saved: ${r.data.linked?.length || 0} linked.`);
    } catch(e) { showMsg(e.response?.data?.error || 'Failed to save client links'); }
    setLinkSaving(false);
  };

  const startEdit = (v) => {
    setForm({ name:v.name||'', contact:v.contact||'', phone:v.phone||'', email:v.email||'', website:v.website||'', notes:v.notes||'', category:v.category||'Supplier', account_number:v.account_number||'', payment_terms:v.payment_terms||'Net 30', rating:v.rating||0, address:v.address||'', city:v.city||'', state:v.state||'', zip:v.zip||'' });
    setEditing(v.id); setShowForm(true);
  };

  const fmt = n => '$' + Number(n||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2});
  const fmtDate = d => d ? new Date(d+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '—';

  const filtered = vendors.filter(v => {
    const q = search.toLowerCase();
    const matchQ = !q || v.name?.toLowerCase().includes(q) || v.contact?.toLowerCase().includes(q) || v.email?.toLowerCase().includes(q);
    const matchCat = !filterCat || v.category === filterCat;
    return matchQ && matchCat;
  });

  const selectedVendor = vendors.find(v => v.id === selected);

  return (
    <div style={{ display:'grid', gridTemplateColumns:'280px 1fr', gap:0, minHeight:600, border:`1px solid ${C.border}`, borderRadius:12, overflow:'hidden' }}>

      {/* Left — vendor list */}
      <div style={{ borderRight:`1px solid ${C.border}`, background:'#1a1c28', display:'flex', flexDirection:'column' }}>
        {/* List header */}
        <div style={{ padding:'16px 14px', borderBottom:`1px solid ${C.border}` }}>
          {flash && <div data-testid="vendor-flash" style={{ marginBottom:10, padding:'8px 10px', borderRadius:6, background:'rgba(96,165,250,0.1)', border:'1px solid rgba(96,165,250,0.3)', color:C.text, fontSize:12, lineHeight:1.4 }}>{flash}</div>}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 }}>
            <div style={{ fontSize:13, fontWeight:700, color:C.text }}>🏭 Vendors <span style={{ color:C.muted, fontWeight:400 }}>({filtered.length})</span></div>
            <button onClick={() => { setShowForm(true); setEditing(null); setForm(blank); }}
              style={{ padding:'5px 12px', borderRadius:6, border:'none', background:C.accent, color:'#fff', fontSize:11, fontWeight:700, cursor:'pointer' }}>+ Add</button>
          </div>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search vendors..."
            style={{ ...inp, fontSize:12, marginBottom:8 }} />
          <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:11, color:C.muted, marginBottom:8, cursor:'pointer' }}>
            <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} style={{ accentColor:C.accent }} /> Show archived
          </label>
          <select value={filterCat} onChange={e=>setFilterCat(e.target.value)} style={{ ...inp, fontSize:11 }}>
            <option value="">All Categories</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {/* Vendor list */}
        <div style={{ flex:1, overflowY:'auto' }}>
          {loading ? (
            <div style={{ padding:20, textAlign:'center', color:C.muted, fontSize:13 }}>Loading...</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding:20, textAlign:'center', color:C.muted, fontSize:13 }}>No vendors found</div>
          ) : filtered.map(v => (
            <div key={v.id} onClick={() => selectVendor(v.id)}
              style={{ padding:'12px 14px', borderBottom:`1px solid ${C.border}`, cursor:'pointer', background: selected===v.id ? 'rgba(249,115,22,0.08)' : 'transparent', borderLeft: selected===v.id ? `3px solid ${C.accent}` : '3px solid transparent', transition:'all 0.1s' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
                <div style={{ fontWeight:700, fontSize:13, color: selected===v.id ? C.accent : C.text }}>
                  {v.name}
                  {v.is_active === false && <span style={{ marginLeft:6, fontSize:9, padding:'1px 6px', borderRadius:4, background:'rgba(148,163,184,0.15)', color:C.muted, fontWeight:700 }}>ARCHIVED</span>}
                </div>
                {v.rating > 0 && <StarRating value={v.rating} />}
              </div>
              {v.category && <div style={{ fontSize:10, color:C.muted, marginTop:2 }}>{v.category}</div>}
              {v.contact && <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>{v.contact}</div>}
              <div style={{ display:'flex', gap:6, marginTop:6 }} onClick={e => e.stopPropagation()}>
                {v.is_active === false ? (
                  <button data-testid={`vendor-restore-${v.id}`} onClick={() => restore(v.id)} style={{ padding:'3px 8px', borderRadius:5, border:`1px solid ${C.border}`, background:'transparent', color:C.green, fontSize:10, cursor:'pointer' }}>Restore</button>
                ) : (
                  <>
                    <button data-testid={`vendor-edit-${v.id}`} onClick={() => { setSelected(v.id); startEdit(v); }} style={{ padding:'3px 8px', borderRadius:5, border:`1px solid ${C.border}`, background:'transparent', color:C.blue, fontSize:10, cursor:'pointer' }}>Edit</button>
                    <button data-testid={`vendor-delete-${v.id}`} onClick={() => del(v.id)} style={{ padding:'3px 8px', borderRadius:5, border:'1px solid #3b0a0a', background:'transparent', color:C.red, fontSize:10, cursor:'pointer' }}>Delete</button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right — detail panel */}
      <div style={{ background:C.card, display:'flex', flexDirection:'column' }}>
        {showForm ? (
          // Add/Edit form
          <div style={{ padding:24, overflowY:'auto' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <div style={{ fontSize:16, fontWeight:800, color:C.text }}>{editing ? 'Edit Vendor' : 'New Vendor'}</div>
              <button onClick={() => { setShowForm(false); setEditing(null); }} style={{ background:'none', border:'none', color:C.muted, fontSize:20, cursor:'pointer' }}>×</button>
            </div>
            {/* Client picker */}
            <div style={{ marginBottom:16, position:'relative' }}>
              <button onClick={() => setShowClientPicker(p=>!p)}
                style={{ width:'100%', padding:'9px 14px', borderRadius:8, border:'1px dashed #2e3150', background:'transparent', color:'#f97316', fontSize:13, fontWeight:600, cursor:'pointer', textAlign:'left', display:'flex', alignItems:'center', gap:8 }}>
                👤 Select from existing client
                <span style={{ marginLeft:'auto', fontSize:10, color:'#64748b' }}>{showClientPicker ? '▲' : '▼'}</span>
              </button>
              {showClientPicker && (
                <div style={{ position:'absolute', top:'calc(100% + 4px)', left:0, right:0, background:'#252838', border:'1px solid #2e3150', borderRadius:10, zIndex:200, boxShadow:'0 12px 32px rgba(0,0,0,0.5)', overflow:'hidden' }}>
                  <div style={{ padding:'10px 12px', borderBottom:'1px solid #2e3150' }}>
                    <input autoFocus value={pickerSearch} onChange={e=>setPickerSearch(e.target.value)}
                      placeholder="Search clients..." style={{ padding:'8px 10px', borderRadius:6, border:'1px solid #2e3150', background:'#181928', color:'#e2e8f0', fontSize:12, width:'100%', boxSizing:'border-box' }} />
                  </div>
                  <div style={{ maxHeight:240, overflowY:'auto' }}>
                    {allClients.filter(c => { const q=pickerSearch.toLowerCase(); return !q||c.name?.toLowerCase().includes(q)||c.company?.toLowerCase().includes(q)||c.email?.toLowerCase().includes(q); }).map(c => (
                      <div key={c.id} onClick={() => {
                        setForm(p => ({ ...p, name:c.name||c.company||p.name, email:c.email||p.email, phone:c.phone||p.phone, address:c.address||p.address, city:c.city||p.city, state:c.state||p.state, zip:c.zip||p.zip }));
                        setSelClients(p => p.includes(c.id) ? p : [...p, c.id]);
                        setShowClientPicker(false); setPickerSearch('');
                      }} style={{ padding:'10px 14px', cursor:'pointer', borderBottom:'1px solid #2e3150' }}
                        onMouseEnter={e=>e.currentTarget.style.background='rgba(249,115,22,0.08)'}
                        onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                        <div style={{ fontSize:13, fontWeight:600, color:'#e2e8f0' }}>{c.name}</div>
                        <div style={{ fontSize:11, color:'#64748b', display:'flex', gap:10, marginTop:2 }}>
                          {c.company && <span>{c.company}</span>}
                          {c.email && <span>{c.email}</span>}
                          {c.phone && <span>{c.phone}</span>}
                        </div>
                      </div>
                    ))}
                    {allClients.filter(c => { const q=pickerSearch.toLowerCase(); return !q||c.name?.toLowerCase().includes(q)||c.company?.toLowerCase().includes(q)||c.email?.toLowerCase().includes(q); }).length === 0 && (
                      <div style={{ padding:20, textAlign:'center', color:'#64748b', fontSize:13 }}>No clients found</div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              {[['name','Vendor Name *','text'],['contact','Contact Person','text'],['phone','Phone','tel'],['email','Email','email'],['website','Website','url'],['account_number','Account #','text']].map(([k,l,t]) => (
                <div key={k} style={{ position:'relative' }}>
                  <div style={{ fontSize:10, color:C.muted, marginBottom:4, textTransform:'uppercase', letterSpacing:'.5px' }}>{l}</div>
                  <input type={t} value={form[k]} onChange={e => {
                    setForm(p=>({...p,[k]:e.target.value}));
                    if (k === 'name' && e.target.value.length > 0) {
                      const q = e.target.value.toLowerCase();
                      setNameSuggestions(allClients.filter(c => c.name?.toLowerCase().includes(q) || c.company?.toLowerCase().includes(q)).slice(0,6));
                    } else if (k === 'name') {
                      setNameSuggestions([]);
                    }
                  }} onBlur={() => setTimeout(()=>setNameSuggestions([]),200)} style={inp} />
                  {k === 'name' && nameSuggestions.length > 0 && (
                    <div style={{ position:'absolute', top:'100%', left:0, right:0, background:'#252838', border:`1px solid ${C.border}`, borderRadius:8, zIndex:100, boxShadow:'0 8px 24px rgba(0,0,0,0.4)', overflow:'hidden' }}>
                      {nameSuggestions.map(c => (
                        <div key={c.id} onClick={() => {
                          setForm(p=>({ ...p, name:c.name||c.company||'', email:c.email||p.email, phone:c.phone||p.phone }));
                          setNameSuggestions([]);
                          setSelClients(p => p.includes(c.id) ? p : [...p, c.id]);
                        }} style={{ padding:'9px 12px', cursor:'pointer', borderBottom:`1px solid ${C.border}`, display:'flex', flexDirection:'column', gap:2 }}
                          onMouseEnter={e=>e.currentTarget.style.background='rgba(249,115,22,0.08)'}
                          onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                          <span style={{ fontSize:13, color:C.text, fontWeight:600 }}>{c.name}</span>
                          {c.company && <span style={{ fontSize:11, color:C.muted }}>{c.company}</span>}
                          {c.email && <span style={{ fontSize:11, color:C.muted }}>{c.email}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div>
                <div style={{ fontSize:10, color:C.muted, marginBottom:4, textTransform:'uppercase', letterSpacing:'.5px' }}>Category</div>
                <select value={form.category} onChange={e=>setForm(p=>({...p,category:e.target.value}))} style={inp}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <div style={{ fontSize:10, color:C.muted, marginBottom:4, textTransform:'uppercase', letterSpacing:'.5px' }}>Payment Terms</div>
                <select value={form.payment_terms} onChange={e=>setForm(p=>({...p,payment_terms:e.target.value}))} style={inp}>
                  {PAYMENT_TERMS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <div style={{ fontSize:10, color:C.muted, marginBottom:4, textTransform:'uppercase', letterSpacing:'.5px' }}>Rating</div>
                <StarRating value={form.rating} onChange={v=>setForm(p=>({...p,rating:v}))} />
              </div>
              <div style={{ gridColumn:'1/-1' }}>
                <div style={{ fontSize:10, color:C.muted, marginBottom:4, textTransform:'uppercase', letterSpacing:'.5px' }}>Address</div>
                <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr', gap:8 }}>
                  <input placeholder="Street" value={form.address} onChange={e=>setForm(p=>({...p,address:e.target.value}))} style={inp} />
                  <input placeholder="City" value={form.city} onChange={e=>setForm(p=>({...p,city:e.target.value}))} style={inp} />
                  <input placeholder="State" value={form.state} onChange={e=>setForm(p=>({...p,state:e.target.value}))} style={inp} />
                  <input placeholder="ZIP" value={form.zip} onChange={e=>setForm(p=>({...p,zip:e.target.value}))} style={inp} />
                </div>
              </div>
              <div style={{ gridColumn:'1/-1' }}>
                <div style={{ fontSize:10, color:C.muted, marginBottom:4, textTransform:'uppercase', letterSpacing:'.5px' }}>Notes</div>
                <textarea value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} style={{ ...inp, height:70, resize:'vertical' }} />
              </div>
            </div>
            <div style={{ display:'flex', gap:10, marginTop:20 }}>
              <button onClick={save} disabled={saving||!form.name.trim()}
                style={{ padding:'9px 24px', borderRadius:8, border:'none', background:C.accent, color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer', opacity:saving||!form.name.trim()?0.5:1 }}>
                {saving ? 'Saving...' : editing ? 'Update Vendor' : 'Add Vendor'}
              </button>
              <button onClick={() => { setShowForm(false); setEditing(null); }}
                style={{ padding:'9px 16px', borderRadius:8, border:`1px solid ${C.border}`, background:'transparent', color:C.muted, fontSize:13, cursor:'pointer' }}>
                Cancel
              </button>
              {editing && (
                <button onClick={() => del(editing)} style={{ marginLeft:'auto', padding:'9px 16px', borderRadius:8, border:'1px solid #3b0a0a', background:'transparent', color:C.red, fontSize:13, cursor:'pointer' }}>
                  Delete Vendor
                </button>
              )}
            </div>
          </div>
        ) : !selected ? (
          <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:12, color:C.muted }}>
            <div style={{ fontSize:40 }}>🏭</div>
            <div style={{ fontSize:14 }}>Select a vendor to view details</div>
            <button onClick={() => { setShowForm(true); setEditing(null); setForm(blank); }}
              style={{ padding:'8px 20px', borderRadius:8, border:'none', background:C.accent, color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer', marginTop:8 }}>+ Add First Vendor</button>
          </div>
        ) : (
          // Detail view
          <>
            {/* Vendor header */}
            <div style={{ padding:'20px 24px', borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
              <div>
                <div style={{ fontSize:18, fontWeight:800, color:C.text, marginBottom:4 }}>{selectedVendor?.name}</div>
                <div style={{ display:'flex', gap:12, flexWrap:'wrap', alignItems:'center' }}>
                  {selectedVendor?.category && <span style={{ fontSize:11, padding:'2px 8px', borderRadius:4, background:'rgba(249,115,22,0.12)', color:C.accent, fontWeight:700 }}>{selectedVendor.category}</span>}
                  {selectedVendor?.rating > 0 && <StarRating value={selectedVendor.rating} />}
                  {selectedVendor?.payment_terms && <span style={{ fontSize:11, color:C.muted }}>{selectedVendor.payment_terms}</span>}
                </div>
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <button onClick={() => startEdit(selectedVendor)}
                  style={{ padding:'6px 14px', borderRadius:7, border:`1px solid ${C.border}`, background:'transparent', color:C.muted, fontSize:12, cursor:'pointer' }}>
                  Edit
                </button>
                {selectedVendor?.is_active === false
                  ? <button onClick={() => restore(selectedVendor.id)} style={{ padding:'6px 14px', borderRadius:7, border:`1px solid ${C.border}`, background:'transparent', color:C.green, fontSize:12, cursor:'pointer' }}>Restore</button>
                  : <button onClick={() => del(selectedVendor.id)} style={{ padding:'6px 14px', borderRadius:7, border:'1px solid #3b0a0a', background:'transparent', color:C.red, fontSize:12, cursor:'pointer' }}>Delete</button>}
              </div>
            </div>

            {/* Detail tabs */}
            <div style={{ display:'flex', gap:2, padding:'0 24px', borderBottom:`1px solid ${C.border}` }}>
              {[['overview','Overview'],['docs','Documents'],['pos','Purchase Orders'],['equipment','Equipment'],['clients','Clients'],['activity','Activity']].map(([v,l]) => (
                <button key={v} onClick={() => setDetailTab(v)}
                  style={{ padding:'10px 14px', border:'none', borderBottom: detailTab===v ? `2px solid ${C.accent}` : '2px solid transparent', background:'transparent', color: detailTab===v ? C.accent : C.muted, fontWeight: detailTab===v ? 700 : 400, fontSize:12, cursor:'pointer' }}>
                  {l}
                </button>
              ))}
            </div>

            {/* Detail content */}
            <div style={{ flex:1, padding:'20px 24px', overflowY:'auto' }}>
              {detLoading ? (
                <div style={{ textAlign:'center', padding:40, color:C.muted }}>Loading...</div>
              ) : (

                // OVERVIEW
                detailTab === 'overview' && detail && (
                  <div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:20 }}>
                      {/* Contact info */}
                      <div style={{ background:C.card2, borderRadius:10, padding:16, border:`1px solid ${C.border}` }}>
                        <div style={{ fontSize:11, fontWeight:700, color:C.accent, textTransform:'uppercase', letterSpacing:'.5px', marginBottom:12 }}>Contact</div>
                        {[['👤',detail.vendor?.contact],['📞',detail.vendor?.phone],['✉️',detail.vendor?.email],['🌐',detail.vendor?.website],['🏢',detail.vendor?.account_number ? `Acct: ${detail.vendor.account_number}` : null]].filter(([,v])=>v).map(([icon,val],i) => (
                          <div key={i} style={{ display:'flex', gap:8, marginBottom:8, fontSize:12, color:C.text }}>
                            <span>{icon}</span><span>{val}</span>
                          </div>
                        ))}
                        {[detail.vendor?.address, detail.vendor?.city, detail.vendor?.state, detail.vendor?.zip].filter(Boolean).length > 0 && (
                          <div style={{ fontSize:12, color:C.text, marginTop:4 }}>
                            📍 {[detail.vendor?.address, detail.vendor?.city, detail.vendor?.state, detail.vendor?.zip].filter(Boolean).join(', ')}
                          </div>
                        )}
                      </div>
                      {/* Stats */}
                      <div style={{ background:C.card2, borderRadius:10, padding:16, border:`1px solid ${C.border}` }}>
                        <div style={{ fontSize:11, fontWeight:700, color:C.accent, textTransform:'uppercase', letterSpacing:'.5px', marginBottom:12 }}>Summary</div>
                        {[
                          ['Purchase Orders', detail.pos?.length || 0],
                          ['Total Spent', fmt(detail.pos?.filter(p=>p.status==='received').reduce((s,p)=>s+Number(p.total||0),0)||0)],
                          ['Equipment Linked', detail.equipment?.length || 0],
                          ['Documents', detail.docs?.length || 0],
                        ].map(([l,v]) => (
                          <div key={l} style={{ display:'flex', justifyContent:'space-between', marginBottom:8, fontSize:12 }}>
                            <span style={{ color:C.muted }}>{l}</span>
                            <span style={{ fontWeight:700, color:C.text }}>{v}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    {detail.vendor?.notes && (
                      <div style={{ background:C.card2, borderRadius:10, padding:16, border:`1px solid ${C.border}` }}>
                        <div style={{ fontSize:11, fontWeight:700, color:C.accent, textTransform:'uppercase', letterSpacing:'.5px', marginBottom:8 }}>Notes</div>
                        <div style={{ fontSize:13, color:C.text, lineHeight:1.6 }}>{detail.vendor.notes}</div>
                      </div>
                    )}
                  </div>
                )
              )}

              {/* DOCUMENTS */}
              {detailTab === 'docs' && detail && (
                <div>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:C.text }}>Documents & Files</div>
                    <button onClick={() => setAddingDoc(true)} style={{ padding:'6px 14px', borderRadius:7, border:'none', background:C.accent, color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer' }}>+ Add Document</button>
                  </div>

                  {addingDoc && (
                    <div style={{ background:C.card2, border:`1px solid ${C.border}`, borderRadius:10, padding:16, marginBottom:16 }}>
                      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:10 }}>
                        <div>
                          <div style={{ fontSize:10, color:C.muted, marginBottom:4 }}>Document Name *</div>
                          <input value={newDoc.name} onChange={e=>setNewDoc(p=>({...p,name:e.target.value}))} style={inp} placeholder="e.g. Warranty - Concrete Grinder" />
                        </div>
                        <div>
                          <div style={{ fontSize:10, color:C.muted, marginBottom:4 }}>Type</div>
                          <select value={newDoc.type} onChange={e=>setNewDoc(p=>({...p,type:e.target.value}))} style={inp}>
                            {DOC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                        <div>
                          <div style={{ fontSize:10, color:C.muted, marginBottom:4 }}>File URL</div>
                          <input value={newDoc.file_url} onChange={e=>setNewDoc(p=>({...p,file_url:e.target.value}))} style={inp} placeholder="https://..." />
                        </div>
                        <div>
                          <div style={{ fontSize:10, color:C.muted, marginBottom:4 }}>Expiry Date</div>
                          <input type="date" value={newDoc.expiry_date} onChange={e=>setNewDoc(p=>({...p,expiry_date:e.target.value}))} style={inp} />
                        </div>
                      </div>
                      <div style={{ display:'flex', gap:8 }}>
                        <button onClick={addDoc} style={{ padding:'7px 16px', borderRadius:7, border:'none', background:C.accent, color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer' }}>Save</button>
                        <button onClick={() => setAddingDoc(false)} style={{ padding:'7px 12px', borderRadius:7, border:`1px solid ${C.border}`, background:'transparent', color:C.muted, fontSize:12, cursor:'pointer' }}>Cancel</button>
                      </div>
                    </div>
                  )}

                  {(detail.docs||[]).length === 0 && !addingDoc ? (
                    <div style={{ textAlign:'center', padding:40, color:C.muted, fontSize:13 }}>No documents yet</div>
                  ) : (detail.docs||[]).map(d => {
                    const isExpired = d.expiry_date && new Date(d.expiry_date) < new Date();
                    const expiringSoon = d.expiry_date && !isExpired && new Date(d.expiry_date) < new Date(Date.now() + 30*86400000);
                    return (
                      <div key={d.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 0', borderBottom:`1px solid ${C.border}` }}>
                        <div style={{ fontSize:24 }}>📄</div>
                        <div style={{ flex:1 }}>
                          <div style={{ fontWeight:700, fontSize:13, color:C.text, display:'flex', alignItems:'center', gap:8 }}>
                            {d.name}
                            <span style={{ fontSize:10, padding:'2px 6px', borderRadius:4, background:'rgba(96,165,250,0.1)', color:C.blue }}>{d.type}</span>
                            {isExpired && <span style={{ fontSize:10, padding:'2px 6px', borderRadius:4, background:'rgba(248,113,113,0.1)', color:C.red, fontWeight:700 }}>EXPIRED</span>}
                            {expiringSoon && <span style={{ fontSize:10, padding:'2px 6px', borderRadius:4, background:'rgba(251,191,36,0.1)', color:C.amber, fontWeight:700 }}>EXPIRING SOON</span>}
                          </div>
                          {d.expiry_date && <div style={{ fontSize:11, color: isExpired?C.red:expiringSoon?C.amber:C.muted }}>Expires {fmtDate(d.expiry_date)}</div>}
                        </div>
                        {d.file_url && <a href={d.file_url} target="_blank" rel="noreferrer" style={{ padding:'4px 10px', borderRadius:6, border:`1px solid ${C.border}`, color:C.blue, fontSize:11, textDecoration:'none' }}>View</a>}
                        <button onClick={() => deleteDoc(d.id)} style={{ padding:'4px 8px', borderRadius:6, border:'1px solid #3b0a0a', background:'transparent', color:C.red, fontSize:11, cursor:'pointer' }}>✕</button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* PURCHASE ORDERS */}
              {detailTab === 'pos' && detail && (
                <div>
                  <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:16 }}>Purchase Orders</div>
                  {(detail.pos||[]).length === 0 ? (
                    <div style={{ textAlign:'center', padding:40, color:C.muted, fontSize:13 }}>No purchase orders for this vendor</div>
                  ) : (detail.pos||[]).map(po => {
                    const SC = { pending:'#fbbf24', ordered:'#60a5fa', received:'#4ade80', cancelled:'#f87171' };
                    return (
                      <div key={po.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 0', borderBottom:`1px solid ${C.border}` }}>
                        <div style={{ flex:1 }}>
                          <div style={{ fontWeight:700, fontSize:13, color:C.text }}>PO #{po.po_number || po.id}</div>
                          <div style={{ fontSize:11, color:C.muted }}>{fmtDate(po.order_date)}</div>
                        </div>
                        <span style={{ fontSize:11, padding:'3px 8px', borderRadius:4, background:`${SC[po.status]||C.muted}18`, color:SC[po.status]||C.muted, fontWeight:700 }}>{po.status}</span>
                        <span style={{ fontWeight:700, color:C.text, fontSize:13 }}>{fmt(po.total)}</span>
                      </div>
                    );
                  })}
                  <div style={{ marginTop:12, padding:'12px 0', borderTop:`1px solid ${C.border}`, display:'flex', justifyContent:'flex-end' }}>
                    <span style={{ fontSize:13, color:C.muted, marginRight:16 }}>Total Spent (received)</span>
                    <span style={{ fontSize:14, fontWeight:800, color:C.accent }}>{fmt((detail.pos||[]).filter(p=>p.status==='received').reduce((s,p)=>s+Number(p.total||0),0))}</span>
                  </div>
                </div>
              )}

              {/* EQUIPMENT */}
              {detailTab === 'equipment' && detail && (
                <div>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:C.text }}>Linked Equipment</div>
                    <button onClick={saveEquipmentLinks} disabled={linkSaving}
                      style={{ padding:'6px 14px', borderRadius:7, border:'none', background:C.accent, color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer', opacity:linkSaving?0.6:1 }}>
                      {linkSaving ? 'Saving...' : 'Save Links'}
                    </button>
                  </div>
                  <input value={equipSearch} onChange={e=>setEquipSearch(e.target.value)} placeholder="Search equipment..."
                    style={{ ...inp, marginBottom:12, fontSize:12 }} />
                  <div style={{ maxHeight:400, overflowY:'auto' }}>
                    {linkData.equipment.filter(e => !equipSearch || e.name.toLowerCase().includes(equipSearch.toLowerCase())).map(e => (
                      <div key={e.id} onClick={() => setSelEquip(p => p.includes(e.id) ? p.filter(x=>x!==e.id) : [...p,e.id])}
                        style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 12px', borderRadius:8, marginBottom:6, cursor:'pointer', background: selEquip.includes(e.id) ? 'rgba(249,115,22,0.1)' : C.card2, border:`1px solid ${selEquip.includes(e.id) ? C.accent : C.border}` }}>
                        <div style={{ width:18, height:18, borderRadius:4, border:`2px solid ${selEquip.includes(e.id)?C.accent:C.muted}`, background:selEquip.includes(e.id)?C.accent:'transparent', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, color:'#fff', flexShrink:0 }}>
                          {selEquip.includes(e.id) && '✓'}
                        </div>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:13, fontWeight: selEquip.includes(e.id)?700:400, color:C.text }}>{e.name}</div>
                          <div style={{ fontSize:10, color:C.muted }}>{e.tracking_mode}</div>
                        </div>
                        {e.vendor_id && e.vendor_id !== selected && <span style={{ fontSize:10, color:C.amber }}>linked elsewhere</span>}
                      </div>
                    ))}
                    {linkData.equipment.length === 0 && <div style={{ textAlign:'center', padding:20, color:C.muted, fontSize:13 }}>No equipment found</div>}
                  </div>
                  <div style={{ marginTop:10, fontSize:11, color:C.muted }}>{selEquip.length} equipment item{selEquip.length!==1?'s':''} selected</div>
                </div>
              )}

              {/* CLIENTS */}
              {detailTab === 'clients' && detail && (
                <div>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:C.text }}>Linked Clients</div>
                    <button onClick={saveClientLinks} disabled={linkSaving}
                      style={{ padding:'6px 14px', borderRadius:7, border:'none', background:C.accent, color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer', opacity:linkSaving?0.6:1 }}>
                      {linkSaving ? 'Saving...' : 'Save Links'}
                    </button>
                  </div>
                  <input value={clientSearch} onChange={e=>setClientSearch(e.target.value)} placeholder="Search clients..."
                    style={{ ...inp, marginBottom:12, fontSize:12 }} />
                  <div style={{ maxHeight:400, overflowY:'auto' }}>
                    {linkData.clients.filter(c => !clientSearch || c.name?.toLowerCase().includes(clientSearch.toLowerCase()) || c.email?.toLowerCase().includes(clientSearch.toLowerCase()) || c.company?.toLowerCase().includes(clientSearch.toLowerCase())).map(c => (
                      <div key={c.id} onClick={() => setSelClients(p => p.includes(c.id) ? p.filter(x=>x!==c.id) : [...p,c.id])}
                        style={{ display:'flex', alignItems:'center', gap:12, padding:'10px 12px', borderRadius:8, marginBottom:6, cursor:'pointer', background: selClients.includes(c.id) ? 'rgba(249,115,22,0.1)' : C.card2, border:`1px solid ${selClients.includes(c.id) ? C.accent : C.border}` }}>
                        <div style={{ width:18, height:18, borderRadius:4, border:`2px solid ${selClients.includes(c.id)?C.accent:C.muted}`, background:selClients.includes(c.id)?C.accent:'transparent', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, color:'#fff', flexShrink:0 }}>
                          {selClients.includes(c.id) && '✓'}
                        </div>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:13, fontWeight: selClients.includes(c.id)?700:400, color:C.text }}>{c.name}</div>
                          <div style={{ fontSize:11, color:C.muted }}>{c.company || c.email}</div>
                        </div>
                      </div>
                    ))}
                    {linkData.clients.length === 0 && <div style={{ textAlign:'center', padding:20, color:C.muted, fontSize:13 }}>No clients found</div>}
                  </div>
                  <div style={{ marginTop:10, fontSize:11, color:C.muted }}>{selClients.length} client{selClients.length!==1?'s':''} selected</div>
                </div>
              )}

              {/* ACTIVITY LOG */}
              {detailTab === 'activity' && detail && (
                <div>
                  <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:16 }}>Activity Log</div>
                  <div style={{ display:'flex', gap:10, marginBottom:20 }}>
                    <input value={newNote} onChange={e=>setNewNote(e.target.value)} onKeyDown={e=>e.key==='Enter'&&addLog()}
                      placeholder="Add a note, call log, or update..."
                      style={{ ...inp, flex:1 }} />
                    <button onClick={addLog} disabled={!newNote.trim()} style={{ padding:'8px 16px', borderRadius:7, border:'none', background:C.accent, color:'#fff', fontSize:12, fontWeight:700, cursor:'pointer', opacity:!newNote.trim()?0.5:1 }}>Add</button>
                  </div>
                  {(detail.logs||[]).length === 0 ? (
                    <div style={{ textAlign:'center', padding:40, color:C.muted, fontSize:13 }}>No activity logged yet</div>
                  ) : (detail.logs||[]).map(l => (
                    <div key={l.id} style={{ display:'flex', gap:12, padding:'12px 0', borderBottom:`1px solid ${C.border}` }}>
                      <div style={{ width:32, height:32, borderRadius:'50%', background:'rgba(249,115,22,0.1)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:14, flexShrink:0 }}>📝</div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:13, color:C.text }}>{l.note}</div>
                        <div style={{ fontSize:11, color:C.muted, marginTop:3 }}>{new Date(l.created_at).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric',hour:'2-digit',minute:'2-digit'})}</div>
                      </div>
                      <button onClick={() => deleteLog(l.id)} style={{ background:'none', border:'none', color:C.muted, cursor:'pointer', fontSize:12 }}>✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
